/**
 * IncomingCallOverlay
 *
 * Desktop  — compact card centred near top-right, below the header.
 * Mobile   — full-screen overlay.
 */

import { PhoneCall, PhoneOff } from '@/shared/ui/icons';
import { UserAvatar } from '../../shared/ui/user-avatar';
import { useCall } from './CallContext';
import { useMediaQuery } from '../../hooks/useMediaQuery';

// ─── Shared identity block ────────────────────────────────────────────────────

function CallerIdentity({ displayName, title, avatarUrl, pulse = true }: {
  displayName: string;
  title?: string;
  avatarUrl?: string;
  pulse?: boolean;
}) {
  return (
    <div className="flex flex-col items-center gap-3">
      <div className="relative flex items-center justify-center">
        {pulse && (
          <span
            className="absolute rounded-full animate-ping opacity-25"
            style={{ width: 88, height: 88, background: 'var(--brand)' }}
          />
        )}
        <UserAvatar
          src={avatarUrl}
          name={displayName}
          size={80}
          border="3px solid rgba(255,255,255,0.25)"
          fallbackBg="rgba(255,255,255,0.15)"
          fallbackColor="white"
          className="relative"
        />
      </div>
      <div className="text-center">
        <p className="font-semibold font-apple text-white" style={{ fontSize: '1.25rem' }}>
          {displayName}
        </p>
        {title && (
          <p className="font-apple text-white/60 mt-0.5" style={{ fontSize: 'var(--text-label)' }}>
            {title}
          </p>
        )}
        <p className="font-apple text-white/50 mt-1" style={{ fontSize: 'var(--text-label)' }}>
          Incoming call…
        </p>
      </div>
    </div>
  );
}

// ─── Shared action buttons ────────────────────────────────────────────────────

function CallActions({ onAnswer, onReject, size = 'md' }: {
  onAnswer: () => void;
  onReject: () => void;
  size?: 'sm' | 'md';
}) {
  const btnSize = size === 'sm' ? 'w-14 h-14' : 'w-20 h-20';
  const iconSize = size === 'sm' ? 'w-5 h-5' : 'w-7 h-7';

  return (
    <div className="flex items-center justify-center gap-12">
      {/* Reject */}
      <div className="flex flex-col items-center gap-2">
        <button
          type="button"
          onClick={onReject}
          className={`flex items-center justify-center ${btnSize} rounded-full bg-red-500 text-white transition-opacity active:opacity-80 cursor-pointer shadow-lg`}
        >
          <PhoneOff className={iconSize} />
        </button>
        <span className="font-apple text-white/60" style={{ fontSize: 'var(--text-search)' }}>Decline</span>
      </div>

      {/* Answer */}
      <div className="flex flex-col items-center gap-2">
        <button
          type="button"
          onClick={onAnswer}
          className={`flex items-center justify-center ${btnSize} rounded-full text-white transition-opacity active:opacity-80 cursor-pointer shadow-lg`}
          style={{ background: 'var(--brand)' }}
        >
          <PhoneCall className={iconSize} />
        </button>
        <span className="font-apple text-white/60" style={{ fontSize: 'var(--text-search)' }}>Answer</span>
      </div>
    </div>
  );
}

// ─── Mobile — full screen ─────────────────────────────────────────────────────

function MobileIncomingScreen() {
  const { remoteUser, answerCall, rejectCall } = useCall();
  const displayName = remoteUser?.displayName || remoteUser?.name || 'Unknown';
  const title       = (remoteUser as any)?.jobTitle || (remoteUser as any)?.position;

  return (
    <div
      className="fixed inset-0 z-[200] flex flex-col items-center justify-between"
      style={{
        background: 'linear-gradient(160deg, #2d1246 0%, #1a0a2e 60%, #0f0620 100%)',
        paddingTop:    'calc(env(safe-area-inset-top, 0px) + 64px)',
        paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 56px)',
      }}
    >
      <p className="font-apple text-white/50 tracking-wide uppercase" style={{ fontSize: '0.65rem', letterSpacing: '0.12em' }}>
        Quantis Call
      </p>

      <CallerIdentity displayName={displayName} title={title} avatarUrl={remoteUser?.avatar} pulse />

      <CallActions onAnswer={answerCall} onReject={rejectCall} />
    </div>
  );
}

// ─── Desktop — compact card top-right ─────────────────────────────────────────

function DesktopIncomingCard() {
  const { remoteUser, answerCall, rejectCall } = useCall();
  const displayName = remoteUser?.displayName || remoteUser?.name || 'Unknown';
  const title       = (remoteUser as any)?.jobTitle || (remoteUser as any)?.position;

  return (
    <div
      className="fixed z-[200] pointer-events-auto"
      style={{
        top:   'calc(var(--qs-web-header-height, 64px) + 8px)',
        right: '16px',
      }}
    >
      <div
        className="w-72 rounded-2xl overflow-hidden border border-white/10"
        style={{
          background: 'linear-gradient(145deg, #3d1860 0%, #1e0d36 100%)',
          boxShadow: '0 16px 48px rgba(0,0,0,0.35)',
        }}
      >
        {/* Pulsing accent bar */}
        <div className="h-0.5 w-full animate-pulse" style={{ background: 'var(--brand)' }} />

        <div className="flex flex-col items-center gap-5 px-6 py-6">
          <CallerIdentity displayName={displayName} title={title} avatarUrl={remoteUser?.avatar} pulse />
          <CallActions onAnswer={answerCall} onReject={rejectCall} size="sm" />
        </div>
      </div>
    </div>
  );
}

// ─── Export ───────────────────────────────────────────────────────────────────

export function IncomingCallOverlay() {
  const { status } = useCall();
  // 820px = iPad Air portrait. Intentional — see ActiveCallBar for
  // rationale (portrait tablets share the phone full-screen call UI).
  const isMobile   = useMediaQuery('(max-width: 820px)');

  if (status !== 'incoming') return null;

  return isMobile ? <MobileIncomingScreen /> : <DesktopIncomingCard />;
}
